import { Flex, Skeleton } from '@chakra-ui/react';
import { isEqual } from 'lodash';
import React, { useMemo } from 'react';

import { BalanceSheetExposure } from './BalanceSheetExposure';
import { FacilitiesOverviewTiles } from './FacilitiesOverviewTiles';
import { FacilitiesTable } from './FacilitiesTable';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import { useUserAuthOrThrow } from '~/contexts/UserAuthProvider';
import { DefaultErrorRoute } from '~/core/DefaultErrorRoute';
import { PermissionGuard } from '~/core/PermissionGuard';
import { filterFacilitiesTableRows } from '~/core/routes/credit-facilities/facilityFiltersDrawer/facilitiesFiltering';
import { FacilitiesFiltersDrawer } from '~/core/routes/credit-facilities/facilityFiltersDrawer/FacilitiesFiltersDrawer';
import { computeTotalsRow } from '~/core/routes/credit-facilities/hooks/useFacilitiesTableData';
import {
  PortfolioFacilitiesProvider,
  usePortfolioFacilitiesStore,
} from '~/core/routes/credit-facilities/PortfolioFacilitiesProvider';
import * as LimitedPartnersEntityConfig from '~/core/routes/limited-partners/EntityConfig';
import { Typography } from '~/packages/common-frontend/components/Typography';
import { UploadDrawerButton } from '~/packages/dataPipeline/ui/uploads/UploadDrawerButton';
import { AliasMatchingAlert } from '~/packages/limitedPartners/ui/AliasMatchingAlert';

const RouteBase = () => {
  const { user } = useUserAuthOrThrow();
  const { facilities } = useCreditFacilitiesOrThrow();
  const {
    error,
    computedRows: allComputedRows,
    isHasBorrowingBaseLoading,
    isBalanceSheetExposureLoading,
    isFacilitiesTableDataLoading,
    totalsRow,
    hasBorrowingBase,
    balanceSheetImpactCalcs,
    attributeValues,
  } = usePortfolioFacilitiesStore((store) => store.facilitiesTableData);

  const filteredComputedRows = usePortfolioFacilitiesStore(
    (store) =>
      filterFacilitiesTableRows(
        store.facilitiesTableData.computedRows,
        store.filters,
      ),
    isEqual,
  );

  const filteredTotalsRow = useMemo(() => {
    if (!allComputedRows.length || !attributeValues) {
      return computeTotalsRow([], attributeValues || []);
    }
    return computeTotalsRow(filteredComputedRows, attributeValues);
  }, [filteredComputedRows, attributeValues, allComputedRows]);

  if (error) throw error;

  return (
    <Flex direction="column" gap="4rem">
      <Flex justifyContent="space-between" alignItems="center">
        <Typography variant="page-title" trim>
          Portfolio
        </Typography>
        <Flex gap="1rem">
          <FacilitiesFiltersDrawer />
          <UploadDrawerButton buttonText="Generate Report" />
        </Flex>
      </Flex>
      <PermissionGuard
        permissions={(context) => {
          if (context.flags.isBorrowerLimitedOrganization) return 'deny';
          return 'allow';
        }}
      >
        {/* *** Portfolio Summary Tiles *** */}
        {isHasBorrowingBaseLoading ? (
          <Skeleton height="8rem" />
        ) : (
          <>
            {hasBorrowingBase && (
              <>
                <FacilitiesOverviewTiles
                  creditFacilityCount={facilities.length}
                  totalsRow={totalsRow}
                  rows={allComputedRows}
                  balanceSheetImpactCalcs={balanceSheetImpactCalcs}
                  // "FacilitiesTableData" is the data behind the "rows" data
                  // so relying on its loading state for display purpose
                  isLoading={isFacilitiesTableDataLoading}
                />
                <BalanceSheetExposure
                  balanceSheetImpactCalcs={balanceSheetImpactCalcs}
                  isLoading={isBalanceSheetExposureLoading}
                />
              </>
            )}
          </>
        )}
      </PermissionGuard>

      {/* *** Facility Table *** */}
      <FacilitiesTable
        hasBorrowingBase={hasBorrowingBase}
        creditFacilities={facilities}
        enableFacilitySelection={user.userType !== 'BORROWER'}
        rows={filteredComputedRows}
        totalsRow={filteredTotalsRow}
        attributeValues={attributeValues}
        isLoading={isFacilitiesTableDataLoading}
      />
    </Flex>
  );
};

export const Route = () => {
  return (
    <RouteErrorBoundary
      resetAfterCatch={false}
      fallback={({ message, showErrorToast }) => {
        showErrorToast(message);
        return <DefaultErrorRoute />;
      }}
    >
      <PermissionGuard permissions={LimitedPartnersEntityConfig.permissions}>
        <AliasMatchingAlert />
      </PermissionGuard>
      <PortfolioFacilitiesProvider>
        <RouteBase />
      </PortfolioFacilitiesProvider>
      {/* add route base for valley permissions guard or new flag */}
    </RouteErrorBoundary>
  );
};
